import React, { FC, useEffect, useState } from 'react';
import { GreenButton } from '~Styles';
import { TroubleShootButtons } from '../AddedPrinter';
import StepsComponent, { TroubleShootPageContent } from '../Components/StepsComponent';

import epsonLightPowerImage from '../../Images/epson-power-light.png';
import phoneToPower from '../../Images/phone-to-power.png';
import epsonEthernetOff from '../../Images/epson-ethernet-off.png';
import epsonEthernetOn from '../../Images/epson-ethernet-on.png';
import epsonEthernetBlinking from '../../Images/epson-ethernet-blinking.gif';
import { useButton, useDeviceInfo, useI18n, useLogger, useSelector } from '~Hooks';
import ContactSupport from '../Components/ContactSupport';

interface Props {
  retryScan: () => void;
  sessionId: string;
}

type STEPS =
  | 'LIGHTS_QUESTION'
  | 'CHECK_POWER_CABLE'
  | 'TEST_POWER'
  | 'CONTACT_SUPPORT'
  | 'LAN_LIGHTS_QUESTION'
  | 'ETHERNET_OFF'
  | 'TEST_OTHER_CABLE'
  | 'RESTART_ROUTER'
  | 'ETHERNET_ON'
  | 'ETHERNET_BLINKING'
  | 'ETHERNET_BLINKING_NEXT'
  | 'RETRY_SEARCH';

const GeneralEpsonGuide: FC<Props> = ({ retryScan, sessionId }) => {
  const logger = useLogger('printer-troubleshooting');
  const deviceInfo = useDeviceInfo();
  const { i18n } = useI18n();
  const [currentStep, setCurrentStep] = useState<STEPS>('LIGHTS_QUESTION');

  useEffect(() => {
    logger.info('Troubleshooting step: ' + currentStep, {
      sessionId,
    });
  }, [currentStep]);

  const isIpad = deviceInfo?.model.includes('iPad');

  const RetryScan = useButton({
    onClick: () => {
      logger.info('Scanning for printers again');
      retryScan();
    },
  });

  const RetryScanButton = (
    <TroubleShootButtons>
      <GreenButton {...RetryScan}>{i18n('Printer.Troubleshooting.RetryScan')}</GreenButton>
    </TroubleShootButtons>
  );

  const steps: {
    [key in STEPS]: TroubleShootPageContent<STEPS>;
  } = {
    LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: epsonLightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'LAN_LIGHTS_QUESTION',
        },
      ],
    },
    LAN_LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LAN.LightStatus.Question'),
      images: [
        {
          src: epsonEthernetOff,
          height: 70,
        },
        {
          src: epsonEthernetOn,
          height: 70,
        },
        {
          src: epsonEthernetBlinking,
          height: 91,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.TS.LAN.LightStatus.Off'),
          next: 'ETHERNET_OFF',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.On'),
          next: 'ETHERNET_ON',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.Blinking'),
          next: 'ETHERNET_BLINKING',
        },
      ],
    },
    ETHERNET_OFF: {
      title: i18n('Printer.TS.LAN.CableConnnected.Question'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_OTHER_CABLE',
        },
      ],
    },
    ETHERNET_ON: {
      title: i18n('Printer.TS.LAN.SameNetwork.Question', isIpad),
      text: i18n('Printer.TS.LAN.SameNetwork.Desc', isIpad),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    ETHERNET_BLINKING: {
      title: i18n('Printer.TS.LAN.CheckCables'),
      text: i18n('Printer.TS.LAN.CheckCables.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Done'),
          next: 'ETHERNET_BLINKING_NEXT',
        },
      ],
    },
    ETHERNET_BLINKING_NEXT: {
      title: i18n('Printer.TS.LAN.CheckLights'),
      text: i18n('Printer.TS.LAN.CheckLights.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    TEST_OTHER_CABLE: {
      title: i18n('Printer.TS.LAN.TryOtherCable'),
      text: i18n('Printer.TS.LAN.TryOtherCable.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    RESTART_ROUTER: {
      title: i18n('Printer.TS.LAN.RestartRouter'),
      text: i18n('Printer.TS.LAN.RestartRouter.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    CHECK_POWER_CABLE: {
      title: i18n('Printer.TS.CablePluggedIn'),
      text: i18n('Printer.TS.CablePluggedIn.Desc'),
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'TEST_POWER',
        },
      ],
    },
    TEST_POWER: {
      title: i18n('Printer.TS.PowerWorking'),
      text: i18n('Printer.TS.PowerWorking.Desc'),
      images: [
        {
          src: phoneToPower,
          height: 250,
        },
      ],
      custom: <>{RetryScanButton}</>,
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    RETRY_SEARCH: {
      title: i18n('Printer.TS.LAN.RetryScan'),
      text: i18n('Printer.TS.LAN.RetryScan.Desc'),
      custom: <>{RetryScanButton}</>,
    },
    CONTACT_SUPPORT: {
      custom: <ContactSupport />,
    },
  };

  return (
    <>
      <StepsComponent<STEPS>
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  );
};

export default GeneralEpsonGuide;
